import { Fragment, useEffect, useId, useRef, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { getLenis } from "@/components/motion/SmoothScroll";
import { MEDIA, photoSources } from "../media";

/** Pick the right language from a { en, ko } content object. */
export function useContent(dict) {
  const { lang } = useLang();
  const raw = dict[lang] || dict.en;
  let hit = TYPESET_CACHE.get(raw);
  if (!hit) { hit = typesetDeep(raw, lang); TYPESET_CACHE.set(raw, hit); }
  return hit;
}

/*
  LINE-BREAK RULES, applied once to every content string (both languages):
  1. No one-word last lines: the last two words of a sentence block are joined with a
     no-break space, so a short final word always travels with its neighbour.
     In Korean this only applies when the last word is short (4 characters or
     fewer, e.g. "중.", "만듭니다."), because a normal Korean word on its own line reads fine
     and joining longer words pushed headings into uneven breaks.
  2. No breaks inside compound words: "one-hour", "라인·공정", "장/시간", "‘완료’까지" are
     glued with invisible word joiners (U+2060) around - · / and quote marks,
     and a word stays with its bracket, e.g. 시간(SMV).
  3. Korean highlights ([[한 땀]]) of up to 14 characters never split.
  Only strings with a space or Hangul are touched, so paths, ids and slot names stay as they are.
  To switch a rule off, edit typeset() below.
*/
const TYPESET_CACHE = new WeakMap();
const WJ = "\u2060";
const NBSP = "\u00A0";
const HANGUL = /[\u3131-\uD79D]/;
export function typeset(str, lang) {
  if (!/\s/.test(str) && !HANGUL.test(str)) return str;
  let s = str
    // 2. glue compound words (letters/digits on both sides)
    .replace(/([\p{L}\p{N}])([-·/])(?=[\p{L}\p{N}])/gu, `$1${WJ}$2${WJ}`)
    .replace(/([\p{L}\p{N}])\((?=[\p{L}\p{N}])/gu, `$1${WJ}(`)
    .replace(/([‘“])(?=\S)/g, `$1${WJ}`)
    .replace(/(\S)([’”])/g, `$1${WJ}$2${WJ}`);
  // 3. keep short Korean highlights together
  if (lang === "ko") s = s.replace(/\[\[(.{1,14}?)\]\]/g, (m, inner) => `[[${inner.replace(/ /g, NBSP)}]]`);
  // 1. join the last two words of each block (blocks are separated by a blank line)
  return s.split(/(\n{2,})/).map((block) => {
    const words = block.trim().split(/\s+/);
    if (words.length < 3) return block;
    const last = words[words.length - 1].replace(/[\[\]\u2060]/g, "");
    const prev = words[words.length - 2].replace(/[\[\]\u2060]/g, "");
    if (last.length > 13 || last.length + prev.length > 22) return block;
    if (lang === "ko" && HANGUL.test(last) && last.replace(/[.,!?)\]’”]/g, "").length > 4) return block;
    return block.replace(/[ \n]+(\S+\s*)$/, `${NBSP}$1`);
  }).join("");
}
function typesetDeep(v, lang) {
  if (typeof v === "string") return typeset(v, lang);
  if (Array.isArray(v)) return v.map((x) => typesetDeep(x, lang));
  if (v && typeof v === "object" && Object.getPrototypeOf(v) === Object.prototype) {
    const o = {}; for (const k of Object.keys(v)) o[k] = typesetDeep(v[k], lang); return o;
  }
  return v;
}
/** Removes the invisible line-break helpers (for text sent in emails or copied). */
export const plainText = (s) => String(s).replace(/\u2060/g, "").replace(/\u00A0/g, " ");

/**
 * Renders content text.
 *   [[words]]   → <em> (highlight; keep it rare)
 *   "\n\n"      → a deliberate line break (<br />)
 *   single "\n" → a normal space, so the browser wraps naturally in both
 *                 languages at every screen size.
 *   KS-101, K SMART V2 … → kept on one line (see KEEP_TOGETHER below).
 */
/** Names that must never break across lines (product and model names). Add more here. */
export const KEEP_TOGETHER = ["KS-101", "K SMART V2", "K SMART SYSTEMS"];
const keepRe = new RegExp(`(${KEEP_TOGETHER.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "g");
const keep = (str, key) => str.split(keepRe).map((bit, j) =>
  KEEP_TOGETHER.includes(bit) ? <span className="nw" key={`${key}-${j}`}>{bit}</span> : <Fragment key={`${key}-${j}`}>{bit}</Fragment>);

export function Rich({ text }) {
  if (!text) return null;
  const blocks = String(text).split(/\n{2,}/);
  return blocks.map((block, i) => (
    <Fragment key={i}>
      {block.replace(/\s*\n\s*/g, " ").split(/(\[\[.+?\]\])/g).map((part, k) =>
        part.startsWith("[[") && part.endsWith("]]") ? <em key={k}>{keep(part.slice(2, -2), k)}</em> : <Fragment key={k}>{keep(part, k)}</Fragment>
      )}
      {i < blocks.length - 1 && <br />}
    </Fragment>
  ));
}

export const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Smooth-scroll to a section id (works with the site's Lenis smooth scroll). */
export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return false;
  const lenis = getLenis?.();
  const offset = -80; // height of the sticky header
  if (lenis) lenis.scrollTo(el, { offset });
  else window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY + offset, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  // move keyboard focus to the section so the next Tab continues from there
  if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
  el.focus({ preventScroll: true });
  return true;
}

/** <a href="#id"> that scrolls smoothly instead of jumping. */
export function HashLink({ href, children, ...rest }) {
  return (
    <a href={href} onClick={(e) => { if (scrollToId(href.replace("#", ""))) e.preventDefault(); }} {...rest}>
      {children}
    </a>
  );
}

/**
 * Responsive photo from the media register (src/site/media.js).
 * `sizes` tells the browser how wide the image is drawn, so phones download
 * the 640 px file and large screens the full-size one.
 */
export function SlotImg({ slot, className, sizes = "100vw", eager = false, decorative = false, alt: altOverride }) {
  const { lang } = useLang();
  const m = MEDIA[slot];
  if (!m || m.type !== "photo") return null;
  const { src, srcSet } = photoSources(m);
  const alt = decorative ? "" : altOverride ?? m.alt?.[lang] ?? m.alt?.en ?? "";
  return (
    <picture data-slot={slot}>
      <source type="image/webp" srcSet={srcSet} sizes={sizes} />
      <img src={src} alt={alt} className={className} loading={eager ? "eager" : "lazy"} decoding="async"
        fetchPriority={eager ? "high" : undefined} style={{ objectPosition: m.focal }} />
    </picture>
  );
}

/**
 * Muted looping video from the media register.
 * - Loads nothing until it comes near the screen (preload="none" + poster).
 * - Plays only while visible; pauses when scrolled away or the tab is hidden.
 * - With "reduce motion" it never autoplays: the poster stays, and a play
 *   button lets the visitor start it.
 * - If the browser blocks autoplay, the poster simply stays visible.
 * Pass `toggle` to show a pause/play button (needed for long loops).
 */
export function SlotVideo({ slot, className, toggle = false, playing: controlled, labels = { pause: "Pause video", play: "Play video" } }) {
  const ref = useRef(null);
  const { lang } = useLang();
  const m = MEDIA[slot];
  const [userPaused, setUserPaused] = useState(prefersReducedMotion);
  const [playing, setPlaying] = useState(false);

  // Controlled mode (hero carousel): the parent decides when this video plays.
  useEffect(() => {
    const v = ref.current;
    if (!v || typeof controlled !== "boolean") return;
    if (controlled && !prefersReducedMotion()) {
      v.preload = "auto"; v.currentTime = 0;
      const p = v.play(); if (p) p.catch(() => {});
    } else v.pause();
  }, [controlled]);

  useEffect(() => {
    const v = ref.current;
    if (!v || typeof controlled === "boolean") return undefined;
    let visible = false;
    const sync = () => {
      if (visible && !document.hidden && !userPaused) {
        if (v.preload !== "auto") v.preload = "auto";
        const p = v.play();
        if (p) p.catch(() => setPlaying(false));
      } else v.pause();
    };
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; sync(); }, { rootMargin: "200px 0px", threshold: 0.01 });
    io.observe(v);
    document.addEventListener("visibilitychange", sync);
    return () => { io.disconnect(); document.removeEventListener("visibilitychange", sync); };
  }, [userPaused, controlled]);

  if (!m || !m.src) return null;
  const alt = m.alt?.[lang] ?? "";
  return (
    <>
      <video
        ref={ref}
        data-slot={slot}
        className={className}
        src={m.src}
        poster={m.poster}
        muted
        loop
        playsInline
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        aria-hidden={alt ? undefined : true}
        aria-label={alt || undefined}
        style={{ objectPosition: m.focal }}
      />
      {toggle && (
        <button type="button" className="vid-toggle" aria-pressed={!playing}
          aria-label={playing ? labels.pause : labels.play}
          onClick={() => { const p = playing; setUserPaused(p); if (!p) ref.current?.play().catch(() => {}); else ref.current?.pause(); }}>
          {playing
            ? <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M3 2h2v8H3zM7 2h2v8H7z" fill="currentColor" /></svg>
            : <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M3 1.5 10 6l-7 4.5z" fill="currentColor" /></svg>}
        </button>
      )}
    </>
  );
}

/** Adds the gentle "rise in" animation to every .rv element inside the page (skipped for reduced motion). */
export function useReveal(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion()) return undefined;
    root.classList.add("js-rv");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } }),
      { rootMargin: "0px 0px -8% 0px" }
    );
    const watch = () => root.querySelectorAll(".rv:not(.in)").forEach((el) => io.observe(el));
    watch();
    const mo = new MutationObserver(watch);
    mo.observe(root, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); };
  }, [rootRef]);
}

/** True while the element is on screen. */
export function useInView(ref, options = { threshold: 0.3 }) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), options);
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);
  return inView;
}

/**
 * Developer helper: add ?slots to the address to outline every photo/video
 * slot with its id, box ratio, kind and recommended size. No effect otherwise.
 */
export function SlotInspector({ rootRef }) {
  const { lang } = useLang();
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("slots")) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;
    document.documentElement.classList.add("show-slots");
    const tags = [];
    const t = setTimeout(() => {
      document.querySelectorAll("[data-slot]").forEach((el) => {
        const id = el.dataset.slot;
        const m = MEDIA[id];
        const host = el.tagName === "PICTURE" ? el.parentElement : el.parentElement;
        if (getComputedStyle(host).position === "static") host.style.position = "relative";
        const tag = document.createElement("span");
        tag.className = `slot-tag kind-${m?.kind || "unknown"}`;
        tag.textContent = `${id} · ${m?.ratio || "?"} · ${m?.kind || "missing"} · ${m?.size || ""}`;
        host.appendChild(tag);
        tags.push(tag);
      });
    }, 500);
    return () => { clearTimeout(t); tags.forEach((x) => x.remove()); document.documentElement.classList.remove("show-slots"); };
  }, [rootRef, lang]);
  return null;
}

/** The K mark (brand symbol). `tone="light"` for light backgrounds. */
/**
 * K mark: a solid stem with two linked arms (mint up, cyan down) ending in rings.
 * The ring holes are cut out with a mask, so the background shows through on any colour.
 * tone="dark" is for dark backgrounds (light stem); tone="light" for light backgrounds.
 */
export function KMarkV2({ size = 22, tone = "dark", pulse = false }) {
  const uid = useId().replace(/:/g, "");
  const stem = tone === "light" ? "#081C2C" : "#F6F8F7";
  return (
    <svg viewBox="100 80 350 420" width={size} height={size} aria-hidden="true" focusable="false" className="kmark">
      <defs>
        <mask id={`kh${uid}`} maskUnits="userSpaceOnUse" x="100" y="80" width="350" height="420">
          <rect x="100" y="80" width="350" height="420" fill="#fff" />
          <circle cx="192" cy="295" r="23" fill="#000" />
          <circle cx="387" cy="145" r="23" fill="#000" />
          <circle cx="387" cy="445" r="23" fill="#000" />
        </mask>
      </defs>
      <g mask={`url(#kh${uid})`}>
        <line x1="155" y1="145" x2="155" y2="445" stroke={stem} strokeWidth="76" strokeLinecap="round" />
        <line x1="192" y1="295" x2="387" y2="145" stroke="#16E6B2" strokeWidth="62" />
        <circle className={pulse ? "node" : undefined} cx="387" cy="145" r="52" fill="#16E6B2" />
        <line x1="192" y1="295" x2="387" y2="445" stroke="#1EC8FF" strokeWidth="70" />
        <circle cx="192" cy="295" r="52" fill="#1EC8FF" />
        <circle className={pulse ? "node b" : undefined} cx="387" cy="445" r="52" fill="#1EC8FF" />
      </g>
    </svg>
  );
}
